import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2Icon, TriangleAlertIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { authControllerVerify } from "@/client/sdk.gen";
import { ApiError } from "@/lib/api-client";
import { useSessionContext } from "@/lib/session";

type VerifySearch = { token?: string };

export const Route = createFileRoute("/login/verify")({
  validateSearch: (search: Record<string, unknown>): VerifySearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: VerifyPage,
});

/** Redeems the magic link and forwards into the application. */
function VerifyPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { refresh } = useSessionContext();
  const [error, setError] = React.useState<string | null>(null);
  const attempted = React.useRef(false);

  React.useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setError("Der Link ist unvollständig.");
      return;
    }

    void (async () => {
      try {
        await authControllerVerify({ body: { token } });
        await refresh();
        void navigate({ to: "/", replace: true });
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Der Link konnte nicht eingelöst werden.",
        );
      }
    })();
  }, [token, navigate, refresh]);

  return (
    <main className="flex min-h-dvh items-center justify-center px-6">
      {error ? (
        <div className="max-w-sm space-y-4 text-center">
          <TriangleAlertIcon className="text-muted-foreground mx-auto size-8" />
          <p className="text-sm">{error}</p>
          <Button onClick={() => void navigate({ to: "/login" })}>
            Neuen Link anfordern
          </Button>
        </div>
      ) : (
        <div className="text-muted-foreground flex items-center gap-3 text-sm">
          <Loader2Icon className="size-4 animate-spin" />
          Anmeldung wird geprüft ...
        </div>
      )}
    </main>
  );
}
